/**
 * Tenant-admin setup store (Phase 44.3).
 *
 * Drives the /setup multi-step flow: load the tenant's profile +
 * catalogue, track dirty state per section, autosave with debounce,
 * poll regeneration jobs, and activate.
 *
 * Debounce is kept inside the store so any editor component can call
 * `updateProfile(patch)` on every keystroke without worrying about
 * throttling — the store buffers the patch, flushes on the debounce
 * timer, and exposes `autosaveState` ("idle" | "saving" | "saved" |
 * "error") for the floating indicator.
 *
 * Why a separate store from the main useStore? Scope: setup state is
 * only meaningful while the tenant-admin is inside /setup. Keeping it
 * out of the global store means the rest of the app doesn't pay the
 * re-render cost of frequent autosave transitions.
 */

import { create } from "zustand";
import * as api from "../services/setup.js";

const PROFILE_DEBOUNCE_MS = 500;
const CATALOGUE_DEBOUNCE_MS = 1000;
const REGEN_POLL_MS = 3000;
const REGEN_MAX_DURATION_MS = 10 * 60 * 1000;

function deepMerge(target, patch) {
  if (patch === null || typeof patch !== "object" || Array.isArray(patch)) {
    return patch;
  }
  if (target === null || typeof target !== "object" || Array.isArray(target)) {
    return patch;
  }
  const out = { ...target };
  for (const [k, v] of Object.entries(patch)) {
    if (v === null) {
      delete out[k];
    } else if (Array.isArray(v)) {
      out[k] = v;
    } else if (typeof v === "object" && typeof out[k] === "object" && !Array.isArray(out[k])) {
      out[k] = deepMerge(out[k], v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

export const useSetupStore = create((set, get) => {
  // Non-reactive state stashed on the closure so set() doesn't trigger
  // re-renders for bookkeeping.
  const timers = { profile: null, catalogue: null, regenPoll: null };
  const pending = { profile: {}, catalogue: {} };
  // Generation counter for the regen poll loop. Every `reset()` bumps
  // this; an in-flight `tick` awaiting describe-execution will check the
  // counter before calling `set()` or scheduling its next iteration. Fixes
  // the leak where the initial synchronous `tick()` kept running after
  // the component that started it unmounted.
  let regenGen = 0;

  // Flush semantics on validation failure: the patch is drained into the
  // PUT request and cleared from `pending` BEFORE the network round-trip.
  // If the server returns validation_failed, the optimistic local merge
  // stays in place (so the user sees the keystrokes they just typed) but
  // `pending` is empty — the rejected patch is NOT re-queued. The user's
  // next keystroke starts a fresh patch; the correcting edit wins. For
  // partial failures (some keys valid, some not) this means the server
  // has the partial and the client thinks it's clean — acceptable at
  // field-edit granularity but worth revisiting if we ever add server-
  // side atomic multi-field transactions.
  async function flushProfile() {
    const patch = pending.profile;
    pending.profile = {};
    if (Object.keys(patch).length === 0) return;
    set({ autosaveState: "saving" });
    try {
      const resp = await api.putProfile(patch);
      set({
        profile: resp.tenantProfile,
        autosaveState: "saved",
        validationErrors: { ...get().validationErrors, profile: null },
      });
    } catch (err) {
      const parsed = parseValidationError(err);
      if (parsed) {
        set({
          autosaveState: "error",
          validationErrors: { ...get().validationErrors, profile: parsed },
        });
      } else {
        set({ autosaveState: "error", error: err.message });
      }
    }
  }

  async function flushCatalogue() {
    const patch = pending.catalogue;
    pending.catalogue = {};
    if (Object.keys(patch).length === 0) return;
    set({ autosaveState: "saving" });
    try {
      const resp = await api.putCatalogue(patch);
      set({
        catalogue: resp.productCatalogue,
        autosaveState: "saved",
        validationErrors: { ...get().validationErrors, catalogue: null },
      });
    } catch (err) {
      const parsed = parseValidationError(err);
      if (parsed) {
        set({
          autosaveState: "error",
          validationErrors: { ...get().validationErrors, catalogue: parsed },
        });
      } else {
        set({ autosaveState: "error", error: err.message });
      }
    }
  }

  function schedule(section) {
    if (timers[section]) clearTimeout(timers[section]);
    const ms = section === "profile" ? PROFILE_DEBOUNCE_MS : CATALOGUE_DEBOUNCE_MS;
    timers[section] = setTimeout(
      section === "profile" ? flushProfile : flushCatalogue,
      ms
    );
  }

  return {
    status: "idle",          // idle | loading | error
    tenantStatus: null,      // setup_review | active | ...
    role: null,              // tenant-admin | super-admin | member
    profile: null,
    catalogue: null,
    validationErrors: { profile: null, catalogue: null },
    regenerationJob: null,   // { executionArn, mode, startedAt }
    regenerationStatus: null, // { jobId, status, mode } — from the poll
    regenerationError: null,
    autosaveState: "idle",   // idle | saving | saved | error
    error: null,

    loadSetup: async () => {
      set({ status: "loading", error: null });
      try {
        const data = await api.getSetup();
        set({
          status: "idle",
          tenantStatus: data.status,
          role: data.role,
          profile: data.tenantProfile,
          catalogue: data.productCatalogue,
          regenerationJob: data.regenerationJob || null,
          validationErrors: { profile: null, catalogue: null },
        });
      } catch (err) {
        set({ status: "error", error: err.message });
      }
    },

    updateProfile: (patch) => {
      pending.profile = deepMerge(pending.profile, patch);
      // Optimistic local merge so the form renders the typed value even
      // while the debounce is in flight.
      set({ profile: deepMerge(get().profile || {}, patch), autosaveState: "idle" });
      schedule("profile");
    },

    updateCatalogue: (patch) => {
      pending.catalogue = deepMerge(pending.catalogue, patch);
      set({ catalogue: deepMerge(get().catalogue || {}, patch), autosaveState: "idle" });
      schedule("catalogue");
    },

    flushNow: async () => {
      if (timers.profile) clearTimeout(timers.profile);
      if (timers.catalogue) clearTimeout(timers.catalogue);
      await Promise.all([flushProfile(), flushCatalogue()]);
    },

    regenerate: async (mode) => {
      set({ regenerationError: null });
      try {
        const resp =
          mode === "profile"
            ? await api.regenerateProfile()
            : await api.regenerateCatalogue();
        set({ regenerationJob: resp.regenerationJob, regenerationStatus: null });
        get()._startPollingRegeneration(mode);
      } catch (err) {
        set({ regenerationError: err.message });
      }
    },

    _startPollingRegeneration: (mode) => {
      const { regenerationJob } = get();
      if (!regenerationJob) return;
      const startedAt = Date.now();
      const arn = regenerationJob.executionArn || "";
      const jobId = arn.split(":").pop();
      if (!jobId) return;

      const myGen = ++regenGen;
      const isCurrent = () => myGen === regenGen;

      const tick = async () => {
        if (!isCurrent()) return;
        if (Date.now() - startedAt > REGEN_MAX_DURATION_MS) {
          if (isCurrent()) set({ regenerationStatus: { jobId, mode, status: "TIMED_OUT_POLL" } });
          return;
        }
        try {
          const status = await api.getRegenerateStatus(jobId);
          if (!isCurrent()) return;
          set({ regenerationStatus: status });
          if (["SUCCEEDED", "FAILED", "TIMED_OUT", "ABORTED"].includes(status.status)) {
            if (status.status === "SUCCEEDED") {
              await get().loadSetup();
            }
            return;
          }
        } catch (err) {
          if (isCurrent()) set({ regenerationError: err.message });
          return;
        }
        if (isCurrent()) timers.regenPoll = setTimeout(tick, REGEN_POLL_MS);
      };
      tick();
    },

    activate: async () => {
      set({ error: null });
      try {
        const updated = await api.activate();
        set({ tenantStatus: updated.status || "active" });
        return true;
      } catch (err) {
        set({ error: err.message });
        return false;
      }
    },

    reopen: async () => {
      try {
        const updated = await api.reopen();
        set({ tenantStatus: updated.status || "setup_review" });
        return true;
      } catch (err) {
        set({ error: err.message });
        return false;
      }
    },

    reset: () => {
      if (timers.profile) clearTimeout(timers.profile);
      if (timers.catalogue) clearTimeout(timers.catalogue);
      if (timers.regenPoll) clearTimeout(timers.regenPoll);
      // Bump the regen generation so any in-flight poll tick that's
      // currently awaiting describe-execution skips its set() + re-schedule
      // on resume.
      regenGen++;
      pending.profile = {};
      pending.catalogue = {};
      set({
        status: "idle",
        tenantStatus: null,
        role: null,
        profile: null,
        catalogue: null,
        validationErrors: { profile: null, catalogue: null },
        regenerationJob: null,
        regenerationStatus: null,
        regenerationError: null,
        autosaveState: "idle",
        error: null,
      });
    },
  };
});

function parseValidationError(err) {
  // `err` is the generic Error thrown by services/api.js on non-2xx.
  // `request()` throws `new Error(\`${status}: ${text}\`)` — the body is
  // in the message. Try to extract a JSON payload.
  try {
    const m = err.message.match(/^\d+:\s*(.+)$/s);
    if (!m) return null;
    const body = JSON.parse(m[1]);
    if (body.error === "validation_failed") {
      return { errors: body.errors || [], warnings: body.warnings || [] };
    }
    if (body.error === "activation_requirements_not_met") {
      return { errors: body.blockers || [] };
    }
    return null;
  } catch {
    return null;
  }
}
